import {
	CSSProperties,
	DragEvent as ReactDragEvent,
	FC,
	Fragment,
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { createPortal } from 'react-dom';
import { useSetRecoilState } from 'recoil';

import { themeModeState } from 'global-stores';

import { setSv2Theme, useSv2Theme } from './use-sv2-theme';

export type SessionVerdict = 'ok' | 'rev' | 'rej' | 'strt' | 'ns';
export type NodeState = 'pass' | 'fail' | 'review' | 'active' | 'pending' | 'unreached' | 'sandbox';

export interface IStep {
	key: string;
	label: string;
	state: NodeState;
	branch?: string;
}

/* ------------------------------------------------------------------ header */

/**
 * The panel has TWO theme systems, and this switch has to drive both — the same
 * rule the Appearance card on General Settings follows.
 *
 * `themeModeState` writes <html data-theme>, which is what every LEGACY screen
 * and component reads (main.scss re-points the whole --color-* family under
 * `:root[data-theme='dark']`). `setSv2Theme` writes <html data-sv2-theme>, which
 * is what the redesigned surfaces read.
 *
 * Flipping only the sv2 half is the bug where a redesigned page went light while
 * everything legacy STANDING ON IT stayed dark: the role cards on User Roles are
 * v1 components hosted in an sv2 deck, and the Invite User / Create New Role
 * dialogs are v1 modals opened from it. The page around them turned white and
 * they did not.
 */
export const ThemeToggle = () => {
	const { isDark } = useSv2Theme();
	const setThemeMode = useSetRecoilState(themeModeState);

	/* Both halves, one click. Deliberately not 'system' — the switch has exactly
	   two positions, and choosing one is an explicit choice to stop following the
	   OS (the Appearance card keeps offering System for that). */
	const toggle = useCallback(() => {
		const next = isDark ? 'light' : 'dark';
		setSv2Theme(next);
		setThemeMode(next);
	}, [isDark, setThemeMode]);

	return (
		<div className="theme-toggle">
			<span className={`tl${isDark ? '' : ' tl--on'}`}>Light</span>
			<button
				className={`tswitch tswitch--${isDark ? 'dark' : 'light'}`}
				type="button"
				onClick={toggle}
				aria-label="Switch between light and dark theme"
			>
				<span className="dots">
					<i />
					<i />
				</span>
				<span className="knob" />
			</button>
			<span className={`tl${isDark ? ' tl--on' : ''}`}>Dark</span>
		</div>
	);
};

/**
 * `action` is the page's primary control (a `.cta` button). It sits between the
 * title block and the theme switch so every redesigned page puts its one
 * page-level action in the same place.
 */
export const PageHead: FC<{ title: string; sub: ReactNode; action?: ReactNode }> = ({
	title,
	sub,
	action,
}) => (
	<div className="hrow">
		<div className="hl">
			<h1>{title}</h1>
			<div className="sub">{sub}</div>
		</div>
		{action}
		<ThemeToggle />
	</div>
);

/* ------------------------------------------------------------------- chips */

/**
 * A step chain. Full labels, never truncated — the whole point of the
 * redesigned flow column is that "Entity/KYB Do…" does not happen.
 */
export const StepChain: FC<{ steps: IStep[] }> = ({ steps }) => (
	<>
		{steps.map((s, i) => (
			<span key={s.key}>
				{i > 0 && <span className="arr">&rarr;</span>}
				<span className={`nd ${s.state === 'pending' ? '' : s.state}`}>{s.label}</span>
				{s.branch ? <span className="bch">{s.branch}</span> : null}
			</span>
		))}
	</>
);

export const StatusChip: FC<{ verdict: SessionVerdict | 'wait' | 'cfg' | 'pur'; children: ReactNode }> =
	({ verdict, children }) => <span className={`vch ${verdict}`}>{children}</span>;

export const StatusLegend = ({ label = 'Status & Onboarding Flow Legends:' }: { label?: string }) => (
	<div className="leg2">
		{label}
		<span className="vch ns">NOT STARTED</span>
		<span className="vch pur">STARTED</span>
		<span className="vch rev">UNDER REVIEW</span>
		<span className="vch ok">APPROVED</span>
		<span className="vch rej">REJECTED</span>
	</div>
);

/* ---------------------------------------------------------------- controls */

export const SearchBox: FC<{
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
	title?: string;
}> = ({ value, onChange, placeholder = 'Search', title }) => (
	<label className="srch" title={title}>
		<svg viewBox="0 0 24 24">
			<circle cx="11" cy="11" r="7" />
			<line x1="16.5" y1="16.5" x2="21" y2="21" />
		</svg>
		<input
			value={value}
			placeholder={placeholder}
			onChange={e => onChange(e.target.value)}
		/>
	</label>
);

export const Pager: FC<{
	page: number;
	pages: number;
	onChange: (page: number) => void;
}> = ({ page, pages, onChange }) => (
	<span className="pgn">
		<button
			type="button"
			aria-label="Previous page"
			disabled={page <= 1}
			onClick={() => onChange(page - 1)}
		>
			&#8249;
		</button>
		<span className="pg">
			{page} of {Math.max(pages, 1)}
		</span>
		<button
			type="button"
			aria-label="Next page"
			disabled={page >= pages}
			onClick={() => onChange(page + 1)}
		>
			&#8250;
		</button>
	</span>
);

export interface ITab {
	key: string;
	label: string;
	count?: number;
	tone?: 'warn' | 'mine';
}

export const Tabs: FC<{
	tabs: ITab[];
	active: string;
	onChange: (key: string) => void;
}> = ({ tabs, active, onChange }) => (
	/* The tone goes on the TAB as well as the badge. It used to sit on the badge
	   alone, which left "Assigned to me" reading in the same grey as every other
	   tab with one coloured chip after it — the tab a reviewer is looking for did
	   not stand out, only its count did. */
	<div className="tabs">
		{tabs.map(t => (
			<span
				key={t.key}
				className={`tab${t.key === active ? ' on' : ''}${t.tone ? ` ${t.tone}` : ''
					}`}
				onClick={() => onChange(t.key)}
			>
				{t.label}
				{typeof t.count === 'number' && t.count > 0 && (
					<span className={`bdg${t.tone ? ` ${t.tone}` : ''}`}>
						{/* NO `99+`. Every tab worth looking at held more than 99, so
						    the strip read "All 99+ · Pending 99+ · Under Review 99+ ·
						    Approved 99+" — four tabs, one number, no information. The
						    zero-padding went with it: it squared up single digits in a
						    fixed-width chip, and real counts vary in width anyway.
						    Grouped, because 1232 is harder to read than 1,232. */}
						{t.count.toLocaleString()}
					</span>
				)}
			</span>
		))}
	</div>
);

/* ------------------------------------------------------------------- kebab */

/**
 * `disabled` + `hint` are additive: a rule that forbids an action should say so
 * where the action is, not hide the action and leave the user guessing why it
 * vanished. "Remove" on the default payment method is the case this exists for
 * — disabled, with the reason printed under it, rather than absent.
 */
/**
 * Where a `position: fixed` box's (0, 0) actually lands on screen.
 *
 * Normally the viewport — but a transformed/filtered/contained ancestor (the
 * detail drawer slides in with `transform`) becomes the containing block for
 * fixed descendants, and if that ancestor scrolls, they scroll with it.
 */
export const fixedOrigin = (el: HTMLElement): { top: number; left: number } => {
	for (let p = el.parentElement; p; p = p.parentElement) {
		const s = getComputedStyle(p);
		if (
			s.transform !== 'none' ||
			s.filter !== 'none' ||
			s.perspective !== 'none' ||
			/paint|layout|strict|content/.test(s.contain) ||
			/transform|filter|perspective/.test(s.willChange)
		) {
			const r = p.getBoundingClientRect();
			return {
				top: r.top + p.clientTop - p.scrollTop,
				left: r.left + p.clientLeft - p.scrollLeft,
			};
		}
	}
	return { top: 0, left: 0 };
};

/**
 * The open menu is `position: fixed`, placed from the button's on-screen rect.
 *
 * It used to hang `absolute` off the button, inside the table's `.tscroll` —
 * which declares only `overflow-x: auto`, but CSS cannot scroll one axis and
 * leave the other visible, so it clips vertically too. Flipping the menu up
 * when the room below ran out only moved the problem: in a one-row table
 * (eVault's documents list) there is no room in EITHER direction, and the menu
 * came out cut in half by the table header. Fixed escapes every clipping
 * ancestor, so the only room that matters is the viewport's.
 */
export const Kebab: FC<{
	items: { label: string; onClick: () => void; disabled?: boolean; hint?: string }[];
}> = ({ items }) => {
	const [open, setOpen] = useState(false);
	const [place, setPlace] = useState<CSSProperties>();
	const wrap = useRef<HTMLSpanElement>(null);
	const menu = useRef<HTMLDivElement>(null);

	/* Measured in a LAYOUT effect, after `open` has put the menu on screen (it is
	   display:none until then, so it has no size to measure) and before the
	   browser paints, so the placement is never visible as a jump. */
	useLayoutEffect(() => {
		if (!open) {
			setPlace(undefined);
			return;
		}
		const w = wrap.current;
		const m = menu.current;
		if (!w || !m) return;
		/* Measure the menu FREE, not boxed into the ~26px `.kwrap`: there the
		   wrapping `.khint` line squeezes it to its min-width, and it grows once
		   fixed — which pushed a menu with a reason line ~45px right of its
		   button, and off the edge of a phone screen. The inline values set here
		   are the ones `place` overwrites (and React clears on close). */
		m.style.position = 'fixed';
		m.style.left = '0px';
		m.style.top = '0px';
		m.style.right = 'auto';
		const rect = w.getBoundingClientRect();
		const origin = fixedOrigin(w);
		const needed = m.offsetHeight + 8;
		const below = window.innerHeight - rect.bottom;
		const above = rect.top;
		/* Only flip when dropping down would run off screen AND there is more room
		   the other way — otherwise a menu near the top would flip into an even
		   tighter space. */
		const up = below < needed && above > below;
		const top = up ? rect.top - 4 - m.offsetHeight : rect.bottom + 4;
		const left = Math.min(
			Math.max(8, rect.right - m.offsetWidth),
			window.innerWidth - m.offsetWidth - 8
		);
		setPlace({
			position: 'fixed',
			top: Math.max(8, top) - origin.top,
			left: left - origin.left,
			right: 'auto',
			bottom: 'auto',
			zIndex: 1000,
		});
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;
		const away = (e: MouseEvent) => {
			if (!wrap.current?.contains(e.target as Node)) setOpen(false);
		};
		/* A fixed menu does not follow its row when anything scrolls, so close it
		   rather than leave it floating over the wrong row. */
		const close = () => setOpen(false);
		document.addEventListener('mousedown', away);
		window.addEventListener('scroll', close, true);
		window.addEventListener('resize', close);
		return () => {
			document.removeEventListener('mousedown', away);
			window.removeEventListener('scroll', close, true);
			window.removeEventListener('resize', close);
		};
	}, [open]);

	const stop = useCallback((e: { stopPropagation: () => void }) => e.stopPropagation(), []);

	return (
		<span className={`kwrap${open ? ' open' : ''}`} ref={wrap} onClick={stop}>
			<button
				className="kebab"
				type="button"
				aria-label="More actions"
				onClick={() => setOpen(o => !o)}
			>
				&#8942;
			</button>
			<div className="kmenu" ref={menu} style={place}>
				{items.map((i, idx) => (
					<Fragment key={i.label}>
						<button
							type="button"
							disabled={i.disabled}
							onClick={() => {
								if (i.disabled) return;
								setOpen(false);
								i.onClick();
							}}
						>
							{i.label}
						</button>
						{/* The reason is printed IN the menu, under the item it explains.
						    It used to be a native `title` tooltip: nothing on the greyed
						    item itself said why, and the tooltip, when it did appear,
						    landed over the table rows below the menu. Consecutive items
						    blocked for the same reason share one line. */}
						{i.disabled && i.hint && items[idx + 1]?.hint !== i.hint && (
							<div className="khint">{i.hint}</div>
						)}
					</Fragment>
				))}
			</div>
		</span>
	);
};

/* ------------------------------------------------------------------ drawer */

export const Drawer: FC<{
	open: boolean;
	label: string;
	children?: ReactNode;
}> = ({ open, label, children }) => (
	<aside className={`drawer${open ? ' open' : ''}`} aria-label={label} aria-hidden={!open}>
		{children}
	</aside>
);

export const DrawerHead: FC<{
	title: string;
	onClose: () => void;
	children?: ReactNode;
	chip?: ReactNode;
}> = ({ title, onClose, children, chip }) => (
	<div className="dhead">
		<div className="dt">
			<h2>{title}</h2>
			{chip}
			<button className="dx" type="button" onClick={onClose} aria-label="Close details">
				&#10005;
			</button>
		</div>
		{children}
	</div>
);

export const Section: FC<{ title: string; right?: ReactNode; children: ReactNode }> = ({
	title,
	right,
	children,
}) => (
	<div className="dsec">
		<div className="sh">
			{title}
			{right ? <span className="r2">{right}</span> : null}
		</div>
		{children}
	</div>
);

/** A muted-label / coloured-value table. Rows with a null value are dropped. */
export const KV: FC<{ rows: [string, ReactNode][] }> = ({ rows }) => (
	<table className="kv">
		<tbody>
			{rows
				.filter(([, v]) => v !== null && v !== undefined && v !== '')
				.map(([k, v], i) => (
					// eslint-disable-next-line react/no-array-index-key
					<tr key={`${k}-${i}`}>
						<td>{k}</td>
						<td>{v}</td>
					</tr>
				))}
		</tbody>
	</table>
);

export const Verified: FC<{ children: ReactNode }> = ({ children }) => (
	<span className="ver">
		{children}
		<svg viewBox="0 0 16 16">
			<path
				d="M2.5 8.5l3.5 3.5 7.5-8.5"
				fill="none"
				stroke="currentColor"
				strokeWidth="2.2"
				strokeLinecap="round"
			/>
		</svg>
	</span>
);

/* ------------------------------------------------- write primitives ------
 * Added with the Settings redesign. The decks above are read-only — they show
 * state and open a drawer. Settings writes, so it needs a dialog, a text
 * field and a copy row. Buttons stay plain classes (`btn`, `btn pri`,
 * `btn danger`), the same way `.cta` is written inline on the decks: the kit
 * wraps what carries behaviour, not what is only a class name.
 */

/** The clipboard glyph, shared by CopyRow and any table cell that copies. */
export const CopyIcon = () => (
	<svg viewBox="0 0 16 16" aria-hidden="true">
		<rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
		<path d="M10.5 5.5v-2a1.5 1.5 0 0 0-1.5-1.5h-5A1.5 1.5 0 0 0 2.5 3.5v5A1.5 1.5 0 0 0 4 10h2" />
	</svg>
);

/**
 * A dialog, portaled to <body>.
 *
 * The portal root carries `sv2-scope sv2-modal` itself because a portaled node
 * is a SIBLING of the page in the real DOM, never a descendant — without those
 * classes on the root, neither the tokens nor the surface primitives resolve
 * inside it. `sv2-modal` rather than `sv2` deliberately: `.sv2` is page
 * geometry (100vh, scrolling), which on a dialog reads as the modal ignoring
 * its own size rules.
 *
 * Closes on Escape and on a click that lands on the backdrop itself.
 */
/**
 * Holds the sv2 theme claim for the lifetime of an open dialog. A component
 * rather than a hook call in `Modal` itself, because `Modal` stays mounted
 * while `open` is false and a hook there would claim the theme permanently.
 */
const ThemeClaim = () => {
	useSv2Theme();
	return null;
};

export const Modal: FC<{
	open: boolean;
	title: string;
	onClose: () => void;
	sub?: ReactNode;
	footer?: ReactNode;
	size?: 'narrow' | 'wide';
	/** Extra classes on the portal root — e.g. `sv2-embed-scope` when the
	 *  body mounts legacy components that read `--color-*` variables. */
	className?: string;
	/** `false` for a dialog the user must finish (a checkout, a journey): no
	 *  close button. Does NOT by itself decide Escape / backdrop — see
	 *  `lightDismiss`. */
	dismissable?: boolean;
	/** Escape and a backdrop click. Defaults to `dismissable`, so the usual
	 *  dialog keeps every way out; a multi-step flow that HAS an X but must not
	 *  be thrown away by a stray keypress passes `false`. Conflating the two
	 *  silently made Escape discard a half-entered journey. */
	lightDismiss?: boolean;
	/** The dialog's accessible name when it shows no visible title. */
	ariaLabel?: string;
	children?: ReactNode;
}> = ({
	open,
	title,
	onClose,
	sub,
	footer,
	size,
	className,
	dismissable = true,
	lightDismiss,
	ariaLabel,
	children,
}) => {
		const canLightDismiss = lightDismiss ?? dismissable;
		const box = useRef<HTMLDivElement>(null);

		useEffect(() => {
			if (!open || !canLightDismiss) return undefined;
			const key = (e: KeyboardEvent) => {
				if (e.key === 'Escape') onClose();
			};
			document.addEventListener('keydown', key);
			return () => document.removeEventListener('keydown', key);
		}, [open, onClose, canLightDismiss]);

		/*
		 * FOCUS: INTO THE DIALOG, TRAPPED INSIDE IT, AND BACK WHERE IT CAME FROM.
		 *
		 * `aria-modal` is an announcement, not a trap — the page behind an overlay
		 * stays in the tab order unless something stops it. react-modal, which the
		 * signup journey used before it moved here, did all three of these by
		 * default; this component did none, so a keyboard user could tab out of the
		 * dialog onto controls hidden underneath it and activate them. On a step
		 * with no close button that left no way back in at all.
		 *
		 * `iframe` is in the selector deliberately: the card fields are Stripe
		 * iframes, and leaving them out would make the whole card form unreachable
		 * by keyboard.
		 */
		useEffect(() => {
			if (!open) return undefined;
			const previous = document.activeElement as HTMLElement | null;
			const focusable = () =>
				Array.from(
					box.current?.querySelectorAll<HTMLElement>(
						'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
					) ?? []
				);
			/* The box itself is the fallback target, so focus never stays behind. */
			(focusable()[0] ?? box.current)?.focus();

			const onKey = (e: KeyboardEvent) => {
				if (e.key !== 'Tab') return;
				const items = focusable();
				if (!items.length) {
					e.preventDefault();
					return;
				}
				const first = items[0];
				const last = items[items.length - 1];
				const active = document.activeElement;
				const outside = !box.current?.contains(active);
				/* `?.` because noUncheckedIndexedAccess types an indexed read as
				   possibly undefined; the length guard above does not narrow it. */
				if (e.shiftKey && (active === first || outside)) {
					e.preventDefault();
					last?.focus();
				} else if (!e.shiftKey && (active === last || outside)) {
					e.preventDefault();
					first?.focus();
				}
			};
			/* Capture, so the trap runs before anything inside the dialog. */
			document.addEventListener('keydown', onKey, true);
			return () => {
				document.removeEventListener('keydown', onKey, true);
				previous?.focus?.();
			};
		}, [open]);

		if (!open) return null;

		return createPortal(
			<div className={`sv2-scope sv2-modal${className ? ` ${className}` : ''}`}>
				{/* The theme is claimed for exactly as long as a dialog is open. It
			    cannot be claimed by the component that OWNS the dialog: the
			    identity flow and the welcome popup are both mounted permanently by
			    the route shell, so a claim there would hold every legacy screen
			    dark forever. `useSv2Theme` refcounts, so nesting dialogs is fine. */}
				<ThemeClaim />
				<div
					className="mdl"
					role="presentation"
					onClick={e => {
						if (canLightDismiss && e.target === e.currentTarget) onClose();
					}}
				>
					<div
						ref={box}
						/* -1 so the box can hold focus when a step has no control of
						   its own, without entering the tab order itself. */
						tabIndex={-1}
						className={`mdlbox${size ? ` ${size}` : ''}`}
						role="dialog"
						aria-modal="true"
						aria-label={ariaLabel || title || undefined}
					>
						{/* A step with no title and no way to close has no header row —
					    an empty strip above the body reads as a rendering fault. */}
						{title || dismissable ? (
							/* `bare` = a header holding nothing but the close button,
							   which must not draw as an empty titled band. */
							<div className={`mdlh${title ? '' : ' bare'}`}>
								<div>
									{title ? <h2>{title}</h2> : null}
									{sub ? <div className="msub">{sub}</div> : null}
								</div>
								{dismissable ? (
									<button className="mdlx" type="button" onClick={onClose} aria-label="Close">
										&#10005;
									</button>
								) : null}
							</div>
						) : null}
						{children ? <div className="mdlb">{children}</div> : null}
						{footer ? <div className="mdlf">{footer}</div> : null}
					</div>
				</div>
			</div>,
			document.body
		);
	};

/**
 * A yes/no question on the system modal: a title, one paragraph, and the two
 * `dbtn`s the design of record puts in modal footers. Replaces the legacy
 * `@storybook` Confirmation on redesigned pages, which paints its own white
 * card and red button and ignores the sv2 tokens (invisible title in dark).
 * `tone` colours the primary button: `pri` for a commitment, `danger` for a
 * destructive one.
 */
export const Confirm: FC<{
	open: boolean;
	title: string;
	body: ReactNode;
	label: string;
	secondaryLabel?: string;
	loading?: boolean;
	tone?: 'pri' | 'danger' | 'risk';
	onConfirm: () => void;
	onClose: () => void;
}> = ({
	open,
	title,
	body,
	label,
	secondaryLabel = 'Cancel',
	loading,
	tone = 'pri',
	onConfirm,
	onClose,
}) => (
		<Modal
			open={open}
			title={title}
			onClose={onClose}
			footer={
				<>
					<button className="dbtn" type="button" onClick={onClose} disabled={loading}>
						{secondaryLabel}
					</button>
					<button
						className={`dbtn ${tone}`}
						type="button"
						onClick={onConfirm}
						disabled={loading}
						aria-busy={loading || undefined}
					>
						{loading ? 'Working…' : label}
					</button>
				</>
			}
		>
			<p className="mdlp">{body}</p>
		</Modal>
	);

/** A labelled text input. `max` also drives the character counter. */
export const Field: FC<{
	label: string;
	value: string;
	onChange: (v: string) => void;
	error?: string;
	hint?: ReactNode;
	max?: number;
	placeholder?: string;
	autoFocus?: boolean;
}> = ({ label, value, onChange, error, hint, max, placeholder, autoFocus }) => (
	<div className={`fld${error ? ' err' : ''}`}>
		<div className="flab">
			{label}
			{max ? (
				<span className="fcount">
					{value.length} / {max}
				</span>
			) : null}
		</div>
		<input
			value={value}
			maxLength={max}
			placeholder={placeholder}
			autoFocus={autoFocus}
			onChange={e => onChange(e.target.value)}
		/>
		{error ? <div className="ferr">{error}</div> : null}
		{!error && hint ? <div className="fhint">{hint}</div> : null}
	</div>
);

/**
 * A credential shown for reading: mono value plus a copy button. `onCopy` is
 * where the page raises its own toast — the kit writes to the clipboard but
 * does not own the app's notification system.
 */
export const CopyRow: FC<{
	label: string;
	value: string;
	note?: ReactNode;
	onCopy?: (value: string) => void;
}> = ({ label, value, note, onCopy }) => (
	<div className="cpy">
		<div className="clab">
			{label}
			<button
				className="cbtn"
				type="button"
				onClick={() => {
					navigator.clipboard.writeText(value);
					onCopy?.(value);
				}}
			>
				<CopyIcon />
				Copy
			</button>
		</div>
		<div className="cval">{value}</div>
		{note ? <div className="cnote">{note}</div> : null}
	</div>
);

/** The whole-page empty state: what this page is, and how to create the first one. */
export const Blank: FC<{ title: string; description: ReactNode; action?: ReactNode }> = ({
	title,
	description,
	action,
}) => (
	<div className="blank">
		<div className="bti">{title}</div>
		<div className="bds">{description}</div>
		{action}
	</div>
);

/**
 * A FILE PICKER: a drag target that is also a click-to-browse input.
 *
 * In the shared kit rather than local to the page that needed it first, because
 * nothing in here was a file picker and the next bulk-upload surface (a KYB
 * document set, a sessions import) wants this exact affordance rather than a
 * second bespoke one.
 *
 * The visible drop zone is a LABEL wrapping a real `<input type="file">`, not a
 * div with a click handler: that is what makes it keyboard-reachable and
 * screen-reader-announced for free, and what lets the browser's own file dialog
 * open without scripting it. The input is visually hidden, never
 * `display: none` — a hidden-by-display input is skipped by the tab order and
 * cannot be focused, so the control would be mouse-only.
 */
export const FileDrop: FC<{
	accept?: string;
	file: File | null;
	onFile: (file: File | null) => void;
	label: string;
	hint?: ReactNode;
	disabled?: boolean;
}> = ({ accept, file, onFile, label, hint, disabled }) => {
	const [dragging, setDragging] = useState(false);

	/* `dragover` must be prevented on EVERY event, not just on drop: the browser
	   default is to navigate to the dropped file, which replaces the whole app
	   with a raw CSV and loses whatever the user had typed. */
	const stop = (e: ReactDragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	return (
		<div className={`fdrop${dragging ? ' fdrop--on' : ''}${disabled ? ' fdrop--off' : ''}`}>
			<label
				className="fdrop__zone"
				onDragEnter={e => {
					stop(e);
					if (!disabled) setDragging(true);
				}}
				onDragOver={stop}
				onDragLeave={e => {
					stop(e);
					setDragging(false);
				}}
				onDrop={e => {
					stop(e);
					setDragging(false);
					if (disabled) return;
					const dropped = e.dataTransfer?.files?.[0] ?? null;
					if (dropped) onFile(dropped);
				}}
			>
				<input
					type="file"
					accept={accept}
					disabled={disabled}
					className="fdrop__input"
					onChange={e => onFile(e.target.files?.[0] ?? null)}
				/>
				<span className="fdrop__label">{file ? file.name : label}</span>
				{file ? (
					<span className="fdrop__meta">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
				) : (
					hint && <span className="fdrop__hint">{hint}</span>
				)}
			</label>
			{file && !disabled && (
				<button
					className="fdrop__clear"
					type="button"
					onClick={() => onFile(null)}
					aria-label={`Remove ${file.name}`}
				>
					Remove
				</button>
			)}
		</div>
	);
};

/**
 * The form switch. `aria-checked` drives the visual state — see `.swt`.
 *
 * `locked` is NOT the same as `disabled`, and the difference is the point. A
 * disabled switch reads as "not available right now"; a locked one means "this
 * cannot be turned on, and the reason is not yours to change" — a country under
 * comprehensive embargo, for instance. It renders a lock glyph so it is
 * distinguishable at a glance from a switch the user themselves turned off, and
 * sets `aria-disabled` alongside `disabled` so the reason reaches a screen
 * reader rather than the control simply being unfocusable and unexplained.
 */
export const Switch: FC<{
	checked: boolean;
	onChange: (next: boolean) => void;
	disabled?: boolean;
	locked?: boolean;
	label: string;
	title?: string;
}> = ({ checked, onChange, disabled, locked, label, title }) => (
	<span className={locked ? 'swtwrap locked' : 'swtwrap'}>
		<button
			className="swt"
			type="button"
			role="switch"
			aria-checked={locked ? false : checked}
			aria-label={label}
			aria-disabled={locked || disabled || undefined}
			title={title}
			disabled={disabled || locked}
			onClick={() => onChange(!checked)}
		/>
		{locked ? (
			<span className="swtlock" aria-hidden="true">
				&#128274;
			</span>
		) : null}
	</span>
);
